import React, { useState, useEffect, useCallback, useMemo } from "react";
import PatientsTemplate from "components/templates/PatientsTemplate";
import { useSelector } from "react-redux";
import { RootState } from "store";
import { Patient, Prescription, PrescriptionGroup } from "common/models/types";
import {
  fetchPatients,
  fetchPatientById,
  savePatient,
  deletePatient,
  fetchPatientPrescriptions,
  savePrescription,
  deletePrescription,
} from "api/patientService";
import {
  groupPrescriptions,
  flattenPrescriptionGroup,
  createEmptyPrescriptionGroup,
  addEmptyPrescriptionToGroup,
  updatePatientPrescriptionGroups,
} from "utils/PrescriptionUtils";
import Spinner from "components/atoms/Spinner";
import Text from "components/atoms/Text";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { COLOR_SYSTEM } from "components/design/design-system";
import Button from "components/atoms/Button";
import { WaitingManager } from "components/organisms/CommonUI/WaitingPopup/WaitingManager";
import { ToastManager } from "components/organisms/CommonUI/ToastPopup/ToastManager";
import PatientForm from "components/organisms/PatientForm";
import { log, LogLevel } from "utils/LogUtil";

const LIMIT = 20; // 한 번에 로드할 항목 수

// 전역 throttleTimer 타입 정의
declare global {
  interface Window {
    throttleTimer: ReturnType<typeof setTimeout> | null;
  }
}

// 초기화
if (typeof window !== "undefined") {
  window.throttleTimer = null;
}

// 스피너 컨테이너
const SpinnerContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.7);
  z-index: 1000;
`;

// 에러 컨테이너
const ErrorContainer = styled.div`
  text-align: center;
  max-width: 600px;
  margin: 100px auto;
  padding: 20px;
  border-radius: 8px;
  background-color: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

// 페이지 컨테이너
const PageContainer = styled.div`
  padding: 24px;
  min-height: 100vh;
  background-color: ${COLOR_SYSTEM.get("Gray")?.[50]};
`;

const Patients: React.FC = () => {
  const navigate = useNavigate();
  // 환자 데이터 상태
  const [patients, setPatients] = useState<Patient[]>([]);
  // 선택된 환자 상태
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  // 현재 검색어
  const [currentSearch, setCurrentSearch] = useState<string | undefined>(undefined);
  // 로딩 상태
  const [isLoading, setIsLoading] = useState<boolean>(true);
  // 에러 상태
  const [error, setError] = useState<string | null>(null);
  // 더 불러올 수 있는지 여부
  const [hasMore, setHasMore] = useState<boolean>(true);
  // 더 로드 중인지 여부
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  // 상세 정보 로딩 중 여부
  const [isLoadingDetail, setIsLoadingDetail] = useState<boolean>(false);
  // 페이지 번호
  const [page, setPage] = useState<number>(1);
  // 처방전 로딩 상태
  const [isPrescriptionLoading, setIsPrescriptionLoading] = useState<boolean>(false);

  // 현재 로그인한 사용자 정보
  const { user } = useSelector((state: RootState) => state.user);

  // 환자 목록 가져오기
  const loadPatients = useCallback(async (pageToLoad = 1, searchTerm?: string) => {
    try {
      const isInitialLoad = pageToLoad === 1;
      if (isInitialLoad) {
        setIsLoading(true);
        // 초기 로드시 선택된 환자 초기화
        setSelectedPatient(null);

        // 초기 로드 시에만 대기 팝업 표시
        WaitingManager.show({
          message: "환자 목록을 불러오는 중입니다...",
        });
      } else {
        setIsLoadingMore(true);
      }

      // 환자 목록 가져오기
      const offset = (pageToLoad - 1) * LIMIT;
      const response = await fetchPatients({
        offset,
        limit: LIMIT,
        search: searchTerm,
      });

      if (response.error) {
        throw new Error(response.message || "환자 목록을 불러오는 데 실패했습니다.");
      }

      const loadedPatients = (response.data as Patient[]) || [];

      if (isInitialLoad) {
        setPatients(loadedPatients);
      } else {
        setPatients((prev) => [...prev, ...loadedPatients]);
      }

      // 더 불러올 데이터가 있는지 확인
      setHasMore(loadedPatients.length === LIMIT);
      setPage(pageToLoad);
    } catch (err) {
      log(LogLevel.ERROR, "환자 목록 로드 오류:", err);
      setError("환자 목록을 불러오는 중 오류가 발생했습니다.");
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);

      // 로딩이 완료되면 대기 팝업 숨기기
      WaitingManager.hide();
    }
  }, []);

  // 컴포넌트 마운트 시 환자 목록 로드
  useEffect(() => {
    loadPatients(1, currentSearch);
  }, [loadPatients, currentSearch]);

  // 환자 선택 핸들러
  const handleSelectPatient = useCallback(
    async (patient: Patient) => {
      try {
        setIsLoadingDetail(true);

        // 이미 선택된 환자 객체를 사용 (API 재호출 불필요)
        const patientData = { ...patient };
        let isDataUpdated = false;

        // 처방전이 없는 경우에만 새로 불러옴
        if (patientData.prescriptions === undefined || patientData.prescriptions === null) {
          // 처방전 정보 가져오기
          const prescriptionsResponse = await fetchPatientPrescriptions(patient.id as number);

          if (prescriptionsResponse.error) {
            console.warn("처방전 정보를 불러오는 데 실패했습니다:", prescriptionsResponse.message);
            patientData.prescriptions = [];
          } else {
            patientData.prescriptions = prescriptionsResponse.data as any[];
          }
          isDataUpdated = true;
        }

        // 처방전 그룹화 - 프론트엔드 전용 데이터 구조
        const patientWithGroups = updatePatientPrescriptionGroups(patientData);

        // 데이터가 업데이트된 경우에만 환자 목록도 함께 업데이트
        if (isDataUpdated) {
          setPatients((prevPatients) =>
            prevPatients.map((p) =>
              p.id === patientWithGroups.id
                ? {
                    ...p,
                    prescriptions: patientWithGroups.prescriptions,
                    prescriptionGroups: patientWithGroups.prescriptionGroups,
                  }
                : p
            )
          );
        }

        // 환자 정보 설정
        setSelectedPatient(patientWithGroups);
      } catch (error) {
        log(LogLevel.ERROR, "환자 상세 정보 로드 오류:", error);
        ToastManager.show({
          content: "환자 정보를 불러오는 데 실패했습니다.",
          duration: 3000,
        });
      } finally {
        setIsLoadingDetail(false);
      }
    },
    [setPatients]
  );

  // 환자 데이터 리로드 핸들러
  const handleReloadPatient = useCallback(
    async (patientId: number) => {
      try {
        setIsLoadingDetail(true);

        // 환자 상세 정보 다시 가져오기
        const response = await fetchPatientById(patientId);

        if (response.error) {
          throw new Error(response.message || "환자 정보를 불러오는 데 실패했습니다.");
        }

        // 환자 기본 정보
        const patientData = response.data as Patient;

        // 처방전 정보 다시 가져오기 (리로드 시에는 항상 최신 데이터 요청)
        const prescriptionsResponse = await fetchPatientPrescriptions(patientId);
        if (prescriptionsResponse.error) {
          console.warn("처방전 정보를 불러오는 데 실패했습니다:", prescriptionsResponse.message);
          patientData.prescriptions = [];
        } else {
          patientData.prescriptions = prescriptionsResponse.data as any[];
        }

        // 처방전 그룹화 - 프론트엔드 전용 데이터 구조
        const patientWithGroups = updatePatientPrescriptionGroups(patientData);

        // 환자 목록도 업데이트
        setPatients((prevPatients) =>
          prevPatients.map((p) =>
            p.id === patientId
              ? {
                  ...p,
                  ...patientWithGroups,
                }
              : p
          )
        );

        // 환자 정보 업데이트
        setSelectedPatient(patientWithGroups);

        // 성공 토스트 메시지 표시
        ToastManager.show({
          content: "환자 정보가 갱신되었습니다.",
          duration: 3000,
        });
      } catch (error) {
        log(LogLevel.ERROR, "환자 정보 리로드 오류:", error);
        ToastManager.show({
          content: "환자 정보를 갱신하는 데 실패했습니다.",
          duration: 3000,
        });
      } finally {
        setIsLoadingDetail(false);
      }
    },
    [setPatients]
  );

  // 환자 정보 저장 핸들러
  const handleUpdatePatient = useCallback(
    async (patient: Patient) => {
      try {
        // 대기 팝업 표시
        WaitingManager.show({
          message: "환자 정보를 저장하는 중입니다...",
        });

        // API 호출 전에 prescriptions, prescriptionGroups 필드 제거
        const patientToSave = { ...patient };
        delete patientToSave.prescriptions;
        delete patientToSave.prescriptionGroups;

        // 환자 정보 저장
        const response = await savePatient(patientToSave);

        if (response.error) {
          throw new Error(response.message || "환자 정보를 저장하는 데 실패했습니다.");
        }

        // 환자 목록 다시 로드 또는 목록에서 해당 항목 업데이트
        if (patient.id) {
          // 기존 환자 정보 업데이트
          setPatients((prevPatients) =>
            prevPatients.map((p) => (p.id === patient.id ? { ...p, ...response.data } : p))
          );

          // 선택된 환자 정보 업데이트 - 기존 처방전은 보존
          const updatedPatient = response.data as Patient;
          setSelectedPatient({
            ...updatedPatient,
            prescriptions: patient.prescriptions,
            prescriptionGroups: patient.prescriptionGroups,
          });
        } else {
          // 새 환자 추가 - 전체 목록 리로드
          await loadPatients(1, currentSearch);

          // 새로 추가된 환자 선택
          setSelectedPatient(response.data as Patient);
        }

        // 성공 토스트 메시지 표시
        ToastManager.show({
          content: "환자 정보가 저장되었습니다.",
          duration: 3000,
        });
      } catch (error) {
        log(LogLevel.ERROR, "환자 정보 저장 오류:", error);
        ToastManager.show({
          content: "환자 정보를 저장하는 데 실패했습니다.",
          duration: 3000,
        });
      } finally {
        // 대기 팝업 숨기기
        WaitingManager.hide();
      }
    },
    [loadPatients, currentSearch]
  );

  // 선택된 환자 변경 핸들러
  const handleSelectPatientChange = useCallback((patient: Patient | null) => {
    setSelectedPatient(patient);
  }, []);

  // 새 환자 생성 핸들러
  const handleCreatePatient = useCallback(async (patientData: {
    birthYear: string;
    gender: string;
    memo: string;
  }) => {
    try {
      // 대기 팝업 표시
      WaitingManager.show({
        message: "새 환자를 생성하는 중입니다...",
      });

      // 출생일 계산 (출생년도의 1월 1일로 설정)
      const birthDate = new Date(parseInt(patientData.birthYear), 0, 1).toISOString();

      // 새 환자 객체 생성
      const newPatient: Patient = {
        birthDate: birthDate,
        gender: patientData.gender,
        pregnancyStatus: false,
        diseases: "",
        allergies: "",
        consultationNotes: "",
        memo: patientData.memo,
      };

      // 환자 생성 API 호출 (실제 API가 있다면 사용)
      // const response = await createPatient(newPatient);
      
      // 임시로 로컬에서 ID 생성
      const tempId = Date.now();
      const createdPatient = { ...newPatient, id: tempId };

      // 환자 목록에 추가
      setPatients(prevPatients => [createdPatient, ...prevPatients]);
      
      // 새 환자 선택
      setSelectedPatient(createdPatient);

      // 성공 토스트 메시지 표시
      ToastManager.show({
        content: "새 환자가 생성되었습니다.",
        duration: 3000,
      });
    } catch (error) {
      log(LogLevel.ERROR, "새 환자 생성 오류:", error);
      ToastManager.show({
        content: "새 환자를 생성하는 데 실패했습니다.",
        duration: 3000,
      });
    } finally {
      // 대기 팝업 숨기기
      WaitingManager.hide();
    }
  }, []);

  // 검색 핸들러
  const handleSearch = useCallback(
    (search?: string) => {
      // 현재 검색어 상태 업데이트
      setCurrentSearch(search);
      // 환자 목록 다시 로드
      loadPatients(1, search);
    },
    [loadPatients]
  );

  // 더 불러오기 핸들러
  const handleLoadMore = useCallback(() => {
    if (!isLoadingMore && hasMore) {
      loadPatients(page + 1, currentSearch);
    }
  }, [isLoadingMore, hasMore, page, loadPatients, currentSearch]);

  // 환자 삭제 핸들러
  const handleDeletePatient = useCallback(
    async (patientId: number) => {
      try {
        // 대기 팝업 표시
        WaitingManager.show({
          message: "환자 정보를 삭제하는 중입니다...",
        });

        // 환자 정보 삭제
        const response = await deletePatient(patientId);

        if (response.error) {
          throw new Error(response.message || "환자 정보를 삭제하는 데 실패했습니다.");
        }

        // 삭제된 환자 정보 미리 저장 (토스트 메시지용)
        const deletedPatient = patients.find((patient) => patient.id === patientId);

        // 로컬 상태에서 환자 제거
        setPatients((prevPatients) => prevPatients.filter((patient) => patient.id !== patientId));

        // 선택된 환자가 삭제된 환자인 경우 선택 해제
        if (selectedPatient?.id === patientId) {
          setSelectedPatient(null);
        }

        // 성공 토스트 메시지 표시
        ToastManager.show({
          content: `환자 정보가 삭제되었습니다.`,
          duration: 3000,
        });
      } catch (error) {
        log(LogLevel.ERROR, "환자 정보 삭제 오류:", error);
        ToastManager.show({
          content: "환자 정보를 삭제하는 데 실패했습니다.",
          duration: 3000,
        });
      } finally {
        // 대기 팝업 숨기기
        WaitingManager.hide();
      }
    },
    [selectedPatient, patients]
  );

  // 처방전 그룹 저장 핸들러
  const handleSavePrescriptionGroup = useCallback(
    async (prescriptionGroup: PrescriptionGroup) => {
      if (!selectedPatient?.id) return;

      try {
        setIsPrescriptionLoading(true);

        // 대기 팝업 표시
        WaitingManager.show({
          message: "처방전을 저장하는 중입니다...",
        });

        log(
          LogLevel.VERBOSE,
          "저장할 처방전 그룹 데이터:",
          JSON.stringify(prescriptionGroup, null, 2)
        );

        // 처방약이 없으면 저장하지 않음
        if (prescriptionGroup.prescriptions.length === 0) {
          throw new Error("저장할 처방 약품이 없습니다.");
        }

        // 모든 처방약에 환자 ID와 처방일 설정
        const prescriptionsToSave = flattenPrescriptionGroup(prescriptionGroup);

        log(LogLevel.VERBOSE, "저장할 처방약 목록:", JSON.stringify(prescriptionsToSave, null, 2));

        // 저장 및 업데이트 병렬 처리
        const savePromises = prescriptionsToSave.map((prescription) => {
          log(LogLevel.VERBOSE, "저장 요청 처방약:", JSON.stringify(prescription, null, 2));
          return savePrescription(prescription);
        });

        // 삭제 처리
        const deletePromises = prescriptionGroup.deletedPrescriptionIds.map((prescriptionId) => {
          log(LogLevel.VERBOSE, "삭제 요청 처방약 ID:", prescriptionId);
          return deletePrescription(prescriptionId);
        });

        // 모든 API 요청 실행
        const results = await Promise.all([...savePromises, ...deletePromises]);

        // 에러 체크
        const hasError = results.some((result) => result.error);
        if (hasError) {
          throw new Error("일부 처방전 저장에 실패했습니다.");
        }

        // 환자 정보 다시 로드
        await handleReloadPatient(selectedPatient.id);

        // 성공 토스트 메시지 표시
        ToastManager.show({
          content: "처방전이 저장되었습니다.",
          duration: 3000,
        });
      } catch (error) {
        log(LogLevel.ERROR, "처방전 저장 오류:", error);
        ToastManager.show({
          content: error instanceof Error ? error.message : "처방전을 저장하는 데 실패했습니다.",
          duration: 3000,
        });
      } finally {
        setIsPrescriptionLoading(false);
        // 대기 팝업 숨기기
        WaitingManager.hide();
      }
    },
    [selectedPatient, handleReloadPatient]
  );

  // 처방전 그룹 삭제 핸들러
  const handleDeletePrescriptionGroup = useCallback(
    async (groupId: string, prescriptions: Prescription[]) => {
      if (!selectedPatient?.id) return;

      try {
        // 대기 팝업 표시
        WaitingManager.show({
          message: "처방전을 삭제하는 중입니다...",
        });

        // id가 있는 처방약만 필터링
        const prescriptionsToDelete = prescriptions.filter((p) => p.id);

        // 삭제 요청 병렬 처리
        const deletePromises = prescriptionsToDelete.map((prescription) =>
          deletePrescription(prescription.id as number)
        );

        if (deletePromises.length > 0) {
          // 모든 삭제 요청 실행
          await Promise.all(deletePromises);
        }

        // 환자 정보 다시 로드
        await handleReloadPatient(selectedPatient.id);

        // 성공 토스트 메시지 표시
        ToastManager.show({
          content: "처방전이 삭제되었습니다.",
          duration: 3000,
        });
      } catch (error) {
        log(LogLevel.ERROR, "처방전 삭제 오류:", error);
        ToastManager.show({
          content: "처방전을 삭제하는 데 실패했습니다.",
          duration: 3000,
        });
      } finally {
        // 대기 팝업 숨기기
        WaitingManager.hide();
      }
    },
    [selectedPatient, handleReloadPatient]
  );

  // 로딩 중인 경우 (첫 로드)
  if (isLoading && patients.length === 0) {
    return (
      <SpinnerContainer>
        <Spinner size="Large" />
      </SpinnerContainer>
    );
  }

  // 에러가 발생한 경우
  if (error) {
    return (
      <ErrorContainer>
        <Text textType="H3">오류가 발생했습니다</Text>
        <p>{error}</p>
        <Button
          buttonType="button"
          color="Primary"
          size="Medium"
          type="Text"
          variant="Contained"
          onClick={() => {
            setError(null);
            loadPatients(1, currentSearch);
          }}
        >
          다시 시도
        </Button>
      </ErrorContainer>
    );
  }

  return (
    <PageContainer>
      <PatientsTemplate
        patients={patients}
        selectedPatient={selectedPatient}
        onSelectPatient={handleSelectPatient}
        onUpdatePatient={handleUpdatePatient}
        onSelectPatientChange={handleSelectPatientChange}
        onReloadPatient={handleReloadPatient}
        onCreatePatient={handleCreatePatient}
        onSearch={handleSearch}
        hasMore={hasMore}
        isLoadingMore={isLoadingMore}
        onLoadMore={handleLoadMore}
        isLoadingDetail={isLoadingDetail}
        onDeletePatient={handleDeletePatient}
        isPrescriptionLoading={isPrescriptionLoading}
        onSavePrescriptionGroup={handleSavePrescriptionGroup}
        onDeletePrescriptionGroup={handleDeletePrescriptionGroup}
      />
    </PageContainer>
  );
};

export default Patients;
